import clsx from 'clsx';
import dayjs from 'dayjs';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { TW_RING } from '../../../../../domain/websiteDomain';
import {
  ClassMinimalFragment,
  FetchSessionsMinimalQueryVariables,
  FetchSessionsQueryVariables,
  useFetchSessionsMinimalQuery,
} from '../../../../../typescript/generated/codegen';
import SideModal from '../../../../common/sideModal/sideModal';
import { LIMIT } from '../../sessionGrid';
import Classifications from './classifications';
import Duration from './duration';
import Intensity from './intensity';
import Level from './level';
import Title from './title';
import Types from './types';

export interface UniqueType {
  classification: string;
  type: string;
}

interface FiltersSideModalProps {
  classifications: string[];
  initialFilters: FetchSessionsQueryVariables;
  onConfirm: (filters: FetchSessionsQueryVariables) => void;
}

const FiltersSideModal: React.FC<FiltersSideModalProps> = ({
  classifications,
  initialFilters,
  onConfirm,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] =
    useState<FetchSessionsMinimalQueryVariables>(initialFilters);
  const [allResults, setAllResults] = useState<ClassMinimalFragment[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [uniqueTypes, setUniqueTypes] = useState<UniqueType[]>([]);

  // find all the different class types by querying all classes
  useFetchSessionsMinimalQuery({
    skip: !isOpen,
    variables: { now: dayjs().toISOString() },
    onCompleted: data => {
      if (data.classCollection?.items) {
        if (data.classCollection.items.length < data.classCollection.total) {
          console.log(
            'not all content has been returned. Maybe consider hardcoding the class types at this point?'
          );
        }

        const types = data.classCollection.items
          // remove all empty types
          .filter(item => !!item.type?.trim() && !!item.classification)
          // create unique array
          .filter(
            (item, index, arr) =>
              arr.findIndex(
                i =>
                  i.classification === item.classification &&
                  i.type === item.type
              ) === index
          )
          // map to structure required for filter sidemodal
          .map(item => ({
            classification: item.classification,
            type: item.type,
          }));

        setUniqueTypes(types as UniqueType[]);
      }
    },
  });

  // get all results for the teacher so that non-available
  // class types are unselectable
  useFetchSessionsMinimalQuery({
    skip: !isOpen,
    variables: { teachers: filters.teachers, now: dayjs().toISOString() },
    onCompleted: data => {
      if (data.classCollection?.items) {
        setAllResults(data.classCollection.items);
      }
    },
  });

  // keep filters in sidemodal in sync with filters in the session grid filter bar
  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters, isOpen]);

  // the query which is called as the filters variable is updated
  useFetchSessionsMinimalQuery({
    skip: !isOpen,
    variables: filters,
    onCompleted: data => setResults(data?.classCollection?.items || []),
  });

  const handleConfirm = () => {
    onConfirm({ ...filters, skip: 0, limit: LIMIT });
    setIsOpen(false);
  };

  const handleClear = () => {
    setIsOpen(false);
    onConfirm({
      ...initialFilters,
      classifications: undefined,
      types: undefined,
      minDuration: undefined,
      maxDuration: undefined,
      levels: undefined,
      skip: 0,
      limit: LIMIT,
    });
  };

  const Footer = () => {
    const hasResults = results.length > 0;

    return (
      <div className="grid grid-cols-2">
        <button className="w-full h-full py-24px" onClick={() => handleClear()}>
          Clear
        </button>
        <button
          className={clsx(
            'h-full w-full py-24px',
            hasResults
              ? 'bg-black text-white'
              : 'bg-gray-300 text-black cursor-not-allowed'
          )}
          onClick={() => hasResults && handleConfirm()}
        >
          {hasResults
            ? `Show ${results.length} class${results.length === 1 ? '' : 'es'}`
            : 'No results'}
        </button>
      </div>
    );
  };

  const areFiltersSet =
    !!initialFilters.levels ||
    !!initialFilters.minDuration ||
    !!initialFilters.maxDuration ||
    !!initialFilters.types;

  const levelTooltip = [
    '1: Beginner',
    '1-2: Beginner - Intermediate',
    '2: Intermediate',
    '2-3: Intermediate - Advanced',
    '3: Advanced',
  ].join('<br />');

  const intensityTooltip = [
    '1: Very Light',
    '2: Light',
    '3: Moderate',
    '4: Vigorous',
    '5: High',
  ].join('<br />');

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={clsx(
          'flex flex-row items-center justify-center w-48px h-48px border border-accent-light rounded-full hover:border-accent',
          areFiltersSet && 'bg-accent-light',
          TW_RING
        )}
        aria-label="Filter"
      >
        <Image src="/svg/filter.svg" height="24" width="24" alt="Filter" />
      </button>

      <SideModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title="Filter classes"
        footer={<Footer />}
      >
        <div className="text-base py-24px px-16px space-y-24px font-family-semibold">
          <div>
            <Title title="Practice" />
            <Classifications
              classifications={classifications}
              filters={filters}
              setFilters={setFilters}
            />
            <Types
              filters={filters}
              setFilters={setFilters}
              uniqueTypes={uniqueTypes}
              allResults={allResults}
            />
          </div>

          <div>
            <Title title="Duration" />
            <Duration filters={filters} setFilters={setFilters} />
          </div>

          <div>
            <Title title="Level" tooltip={levelTooltip} />
            <Level filters={filters} setFilters={setFilters} />
          </div>

          <div>
            <Title title="Intensity" tooltip={intensityTooltip} />
            <Intensity filters={filters} setFilters={setFilters} />
          </div>
        </div>
      </SideModal>
    </>
  );
};

export default FiltersSideModal;
